/** Stable viewport anchors for the translate bubble (avoids live DOMRect drift). */

export function freezeAnchorRect(rect: DOMRect): DOMRect {
  return new DOMRect(rect.x, rect.y, rect.width, rect.height);
}

export function unionRangeClientRect(range: Range): DOMRect {
  const rects = [...range.getClientRects()];
  if (rects.length === 0) return range.getBoundingClientRect();

  let top = Infinity;
  let left = Infinity;
  let bottom = -Infinity;
  let right = -Infinity;
  for (const r of rects) {
    if (r.width === 0 && r.height === 0) continue;
    top = Math.min(top, r.top);
    left = Math.min(left, r.left);
    bottom = Math.max(bottom, r.bottom);
    right = Math.max(right, r.right);
  }
  if (top === Infinity) return range.getBoundingClientRect();
  return new DOMRect(left, top, right - left, bottom - top);
}

function viewportBox(): { top: number; left: number; width: number; height: number } {
  const vv = window.visualViewport;
  return {
    top: vv?.offsetTop ?? 0,
    left: vv?.offsetLeft ?? 0,
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
  };
}

export function isUsableAnchorRect(rect: DOMRect): boolean {
  if (!Number.isFinite(rect.top) || !Number.isFinite(rect.left)) return false;
  if (rect.width > 0 || rect.height > 0) return true;
  // Collapsed selection often reports 0×0 at (0,0) after the highlight is cleared.
  if (rect.top === 0 && rect.left === 0) return false;

  if (typeof window === 'undefined') return false;

  const vp = viewportBox();
  const margin = 80;
  return (
    rect.top >= vp.top - margin &&
    rect.top <= vp.top + vp.height + margin &&
    rect.left >= vp.left - margin &&
    rect.left <= vp.left + vp.width + margin
  );
}

/** Use rect when valid; otherwise a small box centered in the visible viewport. */
export function resolveAnchorRect(rect: DOMRect): DOMRect {
  const frozen = freezeAnchorRect(rect);
  if (isUsableAnchorRect(frozen)) return frozen;

  const vp = viewportBox();
  const w = 80;
  const h = 24;
  return new DOMRect(
    vp.left + vp.width / 2 - w / 2,
    vp.top + vp.height / 2 - h / 2,
    w,
    h
  );
}
